import { useMemo, useState } from 'react'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useIsUS } from '@/hooks/useIsUS'

const DEFAULT_ROWS_PER_PAGE = 5

export function usePatientsSearch() {
  const isUS = useIsUS()

  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [patientSource] = useState(PatientSourceEnum.MedconnectPatient)

  const queryArgs = useMemo(
    () => ({
      limit: DEFAULT_ROWS_PER_PAGE,
      skip: (page - 1) * DEFAULT_ROWS_PER_PAGE,
      orderBy: 'userInfo.surname' as const,
      patientSource,
      smartSearch: searchText,
    }),
    [searchText, page, patientSource]
  )

  const { data, isFetching: isSearching } = useSearchPatientsQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  })

  const patients = useMemo(
    () =>
      data?.items?.map((patient) => ({
        _id: patient._id,
        name: patient.userInfo?.name,
        surname: patient.userInfo?.surname,
        gender: patient.userInfo?.gender,
        birthday: patient.userInfo?.birthday,
        city: patient.contactInfo?.city,
        secu: (isUS ? patient.primaryPolicy : patient.secu) ?? '',
      })) ?? [],
    [data, isUS]
  )

  const onSearch = (searchTerm: string) => {
    setSearchText(searchTerm.replace(/[-']/g, ' ').replace(/\s+/g, ' ').trim())
    setPage(1)
  }

  const onPageChange = (newPage: number) => {
    if (newPage === page) return
    setPage(newPage)
  }

  return {
    patients,
    totalCount: data?.totalCount ?? 0,
    isSearching,
    page,
    onSearch,
    onPageChange,
  }
}
